import { ScopeBlock } from '../blocks/logic/scope-block.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { VariableDefinition } from '../script/variable-definition.js'
import { Block } from '../blocks/block.js'
import { BoRepositoryInspector, BusinessObject } from './business-object.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { LowgileAction } from '../script/lowgile-action.js'
import { BoVisitor } from './bo-visitor.js'
import { Parameter } from '../script/parameter.js'
import { ScreenBlock } from '../blocks/layout/screen-block.js'
import { TextBlock } from '../blocks/other/text-block.js'
import {
	BoSpecificDetails,
	BoSpecificDetailsQuery,
} from './bo-specific-details.js'
import { PlaceholderBlock } from '../blocks/layout/placeholder-block.js'
import { ScreenScrollPositionClass } from './screen-scroll-position-class.js'
import { ScreenOutput } from './screen-output.js'
import { BoReference } from './bo-reference.js'
import { ContentAreaBlock } from '../blocks/layout/content-area-block.js'
import { ComputationDefinition } from '../script/computation-definition.js'
import { BlockComponentImport } from '../blocks/block-component-import.js'
export const ScreenRenderAsTypes = [
	['contents', 'Contents only (default)'],
	['block', 'Block'],
	[
		'inline-block',
		'Inline block (for block-based component screens, e.g. a date picker)',
	],
	['inline', 'Inline (for text-based component screens)'],
] as const
export type ScreenRenderAsType = (typeof ScreenRenderAsTypes)[number][0]

@jsonClass('Screen')
export class Screen extends BusinessObject {
	@jsonArrayProperty(Block)
	blocks: Block[] = []

	@jsonArrayProperty(VariableDefinition)
	variableDefinitions: VariableDefinition[] = []
	@jsonArrayProperty(ComputationDefinition)
	computationDefinitions: ComputationDefinition[] = []
	@jsonArrayProperty(LowgileAction)
	actions: LowgileAction[] = []
	@jsonArrayProperty(ScreenOutput)
	outputs: ScreenOutput[] = []
	@jsonProperty(String)
	renderAs: ScreenRenderAsType = 'contents'
	@jsonProperty(Boolean)
	fillParent: boolean = false
	@jsonProperty(String)
	staticHtmlHeaders: string = ''
	@jsonProperty(String)
	css: string = ''
	@jsonArrayProperty(ScreenScrollPositionClass)
	scrollPositionClasses: ScreenScrollPositionClass[] = []

	maxBlockIdEncountered = 0

	constructor(init?: Partial<Screen>) {
		super()
		assignCommonProperties(this, init)
	}

	initializeNewBo(repositoryInspector: BoRepositoryInspector | null): void {
		this.blocks.push(
			new TextBlock({
				text: `Screen ${this.getQualifiedName()}`,
			}),
		)
		this.actions.push(
			new LowgileAction({
				name: 'onInit',
				code: '',
			}),
		)

		const mainLayoutRef = new BoReference<'Screen'>({
			moduleId: this.moduleId,
			boId: 'MainLayout',
			boType: 'Screen',
		})
		const boSpecificDetails = repositoryInspector?.getBoSpecificDetails(
			mainLayoutRef,
			true,
			{},
		)
		if (boSpecificDetails) {
			const placeholders =
				boSpecificDetails.placeholders?.map(
					(placeholderName) =>
						new ContentAreaBlock({
							name: placeholderName,
						}),
				) ?? []
			const mainPlaceholder =
				placeholders.find((p) => p.name == 'content') ||
				placeholders.find((p) => p.name == 'main') ||
				placeholders[0]
			if (mainPlaceholder) {
				mainPlaceholder.children = this.blocks
				this.blocks = []
			}
			this.blocks = [
				new ScreenBlock({
					screenRef: mainLayoutRef,
					children: [...placeholders, ...this.blocks],
				}),
			]
		}
	}

	getHtmlSelector() {
		return `${this.moduleId}-${this.boId}`
	}

	getBoSpecificDetails(query?: BoSpecificDetailsQuery): BoSpecificDetails {
		const contexts = query?.inputContexts ?? ['input']
		const placeholderBlocks = this.findAllMatchingBlocks(
			(block) => block instanceof PlaceholderBlock,
		) as PlaceholderBlock[]

		return {
			...super.getBoSpecificDetails(query),
			inputs: this.variableDefinitions
				.filter((vd) => contexts.includes(vd.context))
				.map((vd) => ({
					name: vd.name,
					type: vd.type,
					optional: !!vd.defaultExpression,
				})),
			outputs: this.outputs,
			placeholders: placeholderBlocks.map((b) => b.name),
		}
	}

	visitBlocks(visitor: (block: Block) => void) {
		this.blocks.forEach((block, idx) =>
			block.visit(visitor, null, idx, 'first', (b) => b.getChildrenToCompile()),
		)
	}

	findAllMatchingBlocks(blockPredicate: (block: Block) => boolean) {
		const matches: Block[] = []
		this.visitBlocks((block) => {
			if (blockPredicate(block)) {
				matches.push(block)
			}
		})
		return matches
	}

	validateAndFix() {
		this.actions = this.actions.filter((a) => a.name)
		this.maxBlockIdEncountered = 0
		this.visitBlocks(
			(block) =>
				(this.maxBlockIdEncountered = Math.max(
					this.maxBlockIdEncountered,
					block.id || 0,
				)),
		)
		this.visitBlocks((block) => {
			if (!block.id) block.id = ++this.maxBlockIdEncountered
		})

		return {}
	}

	visit(visitor: BoVisitor): void {
		this.blocks.forEach((block, idx) =>
			block.visitWithBoVisitor(visitor, ['blocks', idx]),
		)
		this.actions.forEach((action, idx) =>
			action.visit(visitor, ['actions', idx]),
		)
		this.variableDefinitions.forEach((vd, idx) =>
			vd.visit(visitor, ['variableDefinitions', idx]),
		)
	}

	createVirtualRootBlockAndLinkParents() {
		const rootBlock = new ScopeBlock({
			children: this.blocks,
			// variables: this.variableDefinitions,
			declarationOnlyVariables: [
				// ...this.variableDefinitions,
				new Parameter({ name: 'app', type: 'any' }),
				new Parameter({ name: 'screen', type: 'any' }),
				new Parameter({ name: '$pathSegments', type: 'string[]' }),
				new Parameter({ name: '$disabled', type: 'boolean' }),
			],
		})

		rootBlock.visit(
			(block, parent) => {
				block.parent = parent ?? undefined
			},
			null,
			0,
			'last',
		)

		return rootBlock
	}

	replaceBlock(oldBlock: Block, newBlock: Block) {
		const rootBlock = new ScopeBlock({
			children: this.blocks,
		})

		const success = rootBlock.replaceBlock(oldBlock, newBlock, true)

		this.blocks = rootBlock.children
		return success
	}
}
